import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReplaySubject, catchError, map, of, switchMap, timer } from 'rxjs';
import { LaneSite } from '../../lane-routing-manager/lane.models';
import { RoutingStopService } from './routing-stop.service';

interface LocationRequest {
  query: string;
  mode: 'network' | 'all';
  offset: number;
  delayed: boolean;
}

@Component({
  selector: 'app-routing-stop-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './routing-stop-picker.component.html',
  styleUrls: ['./routing-stops.component.scss']
})
export class RoutingStopPickerComponent implements OnInit {
  @Input() value: LaneSite | null = null;
  @Input() disabled = false;
  @Output() readonly valueChange = new EventEmitter<LaneSite | null>();
  private readonly destroyRef = inject(DestroyRef);
  private readonly requests = new ReplaySubject<LocationRequest>(1);
  results: LaneSite[] = [];
  query = '';
  mode: 'network' | 'all' = 'network';
  offset = 0;
  hasMore = false;
  busy = false;
  error = '';

  constructor(private readonly api: RoutingStopService) {}

  ngOnInit(): void {
    this.requests.pipe(
      switchMap(request => (request.delayed ? timer(250) : of(0)).pipe(
        switchMap(() => this.api.locations(request.query, request.mode, request.offset)),
        map(response => ({ response, error: '' })),
        catchError(error => of({ response: null, error: error.error?.message || 'Locations could not be loaded.' }))
      )),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ response, error }) => {
      this.results = response?.data || [];
      this.hasMore = response?.has_more || false;
      this.error = error;
      this.busy = false;
    });
    this.load();
  }

  load(delayed = false): void {
    this.results = [];
    this.busy = true;
    this.error = '';
    this.requests.next({ query: this.query, mode: this.mode, offset: this.offset, delayed });
  }

  search(): void {
    this.offset = 0;
    this.load(true);
  }

  changeMode(): void {
    this.mode = this.mode === 'network' ? 'all' : 'network';
    this.query = '';
    this.offset = 0;
    this.load();
  }

  page(delta: number): void {
    this.offset = Math.max(0, this.offset + delta);
    this.load();
  }

  chooseIndex(index: string): void {
    const site = index === '' ? null : this.results[Number(index)];
    if (site && !this.disabled) this.valueChange.emit(site);
  }

  clear(): void {
    if (this.disabled) return;
    this.valueChange.emit(null);
    this.load();
  }

  label(site: LaneSite): string {
    return [site.display_name, site.street, [site.city, site.region].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
  }
}
